import { DestroyRef, inject, Inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, catchError, map, Observable, of, tap } from 'rxjs';
import {
	ActionWithContactsGroupRequest,
	ContactGroup,
	ContactGroupsResponse,
} from '@campaign-portal/namespace/entities/contact-groups/specs';
import { exist, Id } from '@campaign-portal/namespace/common/id';
import {
	CreateResponse,
	DeleteResponse,
	ParamsDataIdsRequest,
	ReadResponse,
	UpdateResponse,
} from '@campaign-portal/namespace/common/implementations';
import { RPCRequestParams } from '@campaign-portal/namespace/common/rpc.params';
import {
	CheckCampaignUsageRequest,
	CheckCampaignUsageResponse,
} from '@campaign-portal/namespace/entities/campaigns/specs';
import { RPCResult } from '@campaign-portal/namespace/common/rpc.response';
import {
	AbstractCRUDService,
	AlarisApiService,
	AlarisLanguageService,
	AlarisProfileService,
	AlarisToasterService,
	ErrorNotifierConfig,
	ExtendableRefBookService,
	PROFILE_SERVICE_INJECTOR,
	sortData,
} from '@campaign-portal/components-library';
import { CP_PERMISSIONS } from '../types';
import { ReplyTemplate } from '@campaign-portal/namespace/entities/reply-templates/specs';

type ContactGroupsForAction = ContactGroup<exist> | ContactGroup<exist>[] | null;

function extractGroupIds(group: ContactGroupsForAction): Id<exist>[] | null {
	if (group === null) {
		return null;
	}
	if (Array.isArray(group)) {
		return group.map((item) => item.id);
	}
	return [group.id];
}

@Injectable({
	providedIn: 'root',
})
export class ContactGroupsService extends ExtendableRefBookService<ContactGroup<exist>> implements AbstractCRUDService {
	removedMap$ = new BehaviorSubject<Map<exist, ContactGroup<exist>>>(new Map());
	removedList$ = new BehaviorSubject<ContactGroup<exist>[]>([]);

	private readonly destroyRef = inject(DestroyRef);

	readonly create = this.update;

	constructor(
		private readonly api: AlarisApiService,
		private readonly alarisToaster: AlarisToasterService,
		private readonly langService: AlarisLanguageService,
		@Inject(PROFILE_SERVICE_INJECTOR) private readonly profileService: AlarisProfileService
	) {
		super();
	}

	get entity(): string {
		return this.langService.translate('notifications.entities.group');
	}

	get title(): string {
		return this.langService.translate('notifications.titles.group');
	}

	override load(): Observable<ReadResponse<ContactGroup<exist>[]>> {
		if (this.profileService.allowed([CP_PERMISSIONS.CONTACTS_R])) {
			return this.api
				.loader<ReadResponse<ContactGroup<exist>[]>>('ContactGroups.Read', undefined, this.loading$, this.errorNotifier)
				.pipe(
					map((resp) => {
						sortData(resp.Data, 'name');
						return super.process(resp);
					})
				);
		}
		return of({ Success: true, Total: 0, Data: [] });
	}

	loadRemoved(): Observable<ReadResponse<ContactGroup<exist>[]>> {
		if (this.profileService.allowed([CP_PERMISSIONS.CONTACTS_R])) {
			return this.api
				.loader<ReadResponse<ContactGroup<exist>[]>>(
					'ContactGroups.DeletedGroups',
					undefined,
					this.loading$,
					this.errorNotifier
				)
				.pipe(
					map((resp) => {
						sortData(resp.Data, 'name');
						this.removedList$.next(resp.Data);
						this.removedMap$.next(
							resp.Data.reduce((result, item) => {
								result.set(item.id, item);
								return result;
							}, new Map())
						);
						return resp;
					})
				);
		}
		return of({ Success: true, Total: 0, Data: [] });
	}

	read(params?: RPCRequestParams): Observable<ReadResponse<ContactGroup<exist>[]>> {
		return this.api.loader<ReadResponse<ContactGroup<exist>[]>>(
			'ContactGroups.Read',
			params,
			this.loading$,
			this.errorNotifier
		);
	}

	update(
		group: ContactGroup
	): Observable<CreateResponse<[ContactGroup<exist>]> | UpdateResponse<[ContactGroup<exist>]>> {
		const notify = (response: CreateResponse<[ContactGroup<exist>]>): void => {
			const message = this.langService.translate(
				group.id ? 'notifications.actions.update' : 'notifications.actions.create',
				{
					entity: this.entity,
					name: group.name ?? '',
				}
			);
			if (response.Success) {
				this.alarisToaster.success(message, this.title);
			}
		};

		return this.api
			.loader<CreateResponse<[ContactGroup<exist>]>>(
				'ContactGroups.' + (group.id === null ? 'Create' : 'Update'),
				{ Data: { Entities: [group] } },
				this.loading$,
				this.errorNotifier,
				notify
			)
			.pipe(
				tap(() => {
					this.refresh$.next();
				})
			);
	}

	delete(group: ContactGroupsForAction, { Filters }: RPCRequestParams = {}): Observable<DeleteResponse<ContactGroup>> {
		const params: ParamsDataIdsRequest = {
			Filters,
			Data: {
				Ids: extractGroupIds(group),
			},
		};
		const notify = (response: DeleteResponse<ContactGroup>): void => {
			const message = this.langService.translate('notifications.actions.delete', { entity: this.entity });
			if (response.Success) {
				this.alarisToaster.success(message, this.title);
			}
		};
		return this.api
			.loader<DeleteResponse<ContactGroup>>('ContactGroups.Delete', params, this.loading$, this.errorNotifier, notify)
			.pipe(
				tap(() => {
					this.refresh$.next();
					this.loadRemoved()
						.pipe(
							catchError(() => of(null)),
							takeUntilDestroyed(this.destroyRef)
						)
						.subscribe();
				})
			);
	}

	checkCampaignUsage(
		group: ContactGroupsForAction,
		params: RPCRequestParams = {}
	): Observable<CheckCampaignUsageResponse> {
		const _params: CheckCampaignUsageRequest = {
			Data: {
				Ids: extractGroupIds(group),
			},
			Filters: group === null ? params.Filters : [],
		};
		return this.api.loader<CheckCampaignUsageResponse>(
			'ContactGroups.CheckCampaignUsage',
			_params,
			this.loading$,
			this.errorNotifier
		);
	}

	checkReplyTemplatesUsage(
		group: ContactGroup<exist> | ContactGroup<exist>[] | null,
		params: RPCRequestParams = {}
	): Observable<ReadResponse<ReplyTemplate<exist>[]>> {
		const _params: ParamsDataIdsRequest = {
			Data: {
				Ids: extractGroupIds(group),
			},
			Filters: group === null ? params.Filters : [],
		};
		return this.api.loader<ReadResponse<ReplyTemplate<exist>[]>>(
			'ContactGroups.CheckReplyTemplatesUsage',
			_params,
			this.loading$,
			this.errorNotifier
		);
	}

	moveContacts(
		fromGroup: ContactGroupsForAction,
		toGroup: ContactGroup<exist>,
		{ Filters }: RPCRequestParams
	): Observable<RPCResult<ContactGroup>> {
		const params: ActionWithContactsGroupRequest = {
			Filters,
			Data: {
				FromGroups: extractGroupIds(fromGroup),
				ToGroup: toGroup.id,
			},
		};
		const notify = (response: RPCResult<ContactGroup>): void => {
			const message = this.langService.translate('notifications.actions.moveContacts', { entity: this.entity });
			if (response.Success) {
				this.alarisToaster.success(message, this.title);
			}
		};
		return this.api
			.loader<RPCResult<ContactGroup>>('ContactGroups.MoveContacts', params, this.loading$, this.errorNotifier, notify)
			.pipe(
				tap(() => {
					this.refresh$.next();
				})
			);
	}

	copyContacts(
		fromGroup: ContactGroupsForAction,
		toGroup: ContactGroup<exist>,
		{ Filters }: RPCRequestParams
	): Observable<RPCResult<ContactGroup>> {
		const params: ActionWithContactsGroupRequest = {
			Filters,
			Data: {
				FromGroups: extractGroupIds(fromGroup),
				ToGroup: toGroup.id,
			},
		};
		const notify = (response: RPCResult<ContactGroup>): void => {
			const message = this.langService.translate('notifications.actions.copyContacts', { entity: this.entity });
			if (response.Success) {
				this.alarisToaster.success(message, this.title);
			}
		};
		return this.api
			.loader<RPCResult<ContactGroup>>('ContactGroups.CopyContacts', params, this.loading$, this.errorNotifier, notify)
			.pipe(
				tap(() => {
					this.refresh$.next();
				})
			);
	}

	analysis(group: Id<exist>[] | null): Observable<ContactGroupsResponse> {
		const params: ParamsDataIdsRequest = {
			Data: {
				Ids: Array.isArray(group) ? group : this.list.map((g) => g.id),
			},
		};
		return this.api.loader<ContactGroupsResponse>('ContactGroups.Analysis', params, this.loading$, this.errorNotifier);
	}

	private readonly errorNotifier = (): ErrorNotifierConfig => ({ title: this.title });
}
